"use client";

/**
 * Fires `subscription_created` exactly once when the page is loaded
 * with the post-Stripe `?checkout=success` query param. Used on
 * /compass (where Stripe redirects after successful Compass checkout).
 *
 * The Reports product uses a different success URL pattern
 * (`/account?upgraded=true`) and that page wires its own tracker
 * inline. Both call sites converge on `subscription_created` so the
 * funnel-analytics view in PostHog can roll them up across products.
 *
 * Stays mounted for the lifetime of the page — re-renders are a
 * no-op via the `fired` ref. Removing the query param via router
 * replace is cosmetic and out of scope here (the brand badge is
 * what users see; the URL bar is incidental).
 */

import { useEffect, useRef } from "react";
import { useSearchParams } from "next/navigation";
import { trackEvent, setUserProperties } from "@/lib/posthog";

interface SubscriptionCreatedTrackerProps {
  product: "compass" | "reports";
  /** Tier slug for PostHog (e.g., "pro", "premium"). Used on people-set. */
  tier?: string;
}

export function SubscriptionCreatedTracker({
  product,
  tier,
}: SubscriptionCreatedTrackerProps) {
  const searchParams = useSearchParams();
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    const flag = searchParams?.get("checkout");
    if (flag !== "success") return;
    fired.current = true;
    trackEvent("subscription_created", {
      product,
      ...(tier ? { tier } : {}),
    });
    if (tier) {
      setUserProperties({
        [`tier_${product}`]: tier,
      });
    }
  }, [searchParams, product, tier]);

  return null;
}
